import { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useSnackbar } from 'notistack'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { parseIsoZonedDateTime, formatZonedDateTime } from '@portal/pkg-format/date'
import { OverflowCell } from '@portal/pkg-ui'
import { DashboardQueries } from '../queries'
import { DashboardType } from '../types'
import type { DashboardListItem } from '../types'
import { TypeBadge } from './TypeBadge'
import { DeleteDashboardDialog } from './DeleteDashboardDialog'

const EM_DASH = '—'

/** How many role names fit before the rest collapse into a "+N". */
const ROLES_SHOWN = 3

function LastUpdated({ value }: { value: string }) {
  const { i18n } = useTranslation()
  if (!value) return <>{EM_DASH}</>
  return <>{formatZonedDateTime(parseIsoZonedDateTime(value), i18n.language)}</>
}

/**
 * Comma-separated role names, overflowing to "Deal Manager, Admin, Deal Editor +3".
 *
 * Ids with no matching role are dropped rather than shown raw — a role deleted out from under a
 * dashboard should read as one fewer role, not as a number.
 *
 * The roles lookup is gated on the row actually having ids to resolve. `DashboardListItemResponse`
 * does not carry `roleIds` yet (see the note in `../types`), so today every instance is disabled
 * and the request is never made; it starts resolving on its own once the field is served.
 */
function RolesCell({ roleIds }: { roleIds?: number[] | null }) {
  const hasRoleIds = Boolean(roleIds?.length)
  const { data: roles } = useQuery({ ...DashboardQueries.roles(), enabled: hasRoleIds })
  if (!hasRoleIds || !roles) return <>{EM_DASH}</>

  const names = (roleIds ?? [])
    .map((id) => roles.find((role) => role.id === id)?.name)
    .filter((name): name is string => Boolean(name))
  if (names.length === 0) return <>{EM_DASH}</>

  const shown = names.slice(0, ROLES_SHOWN).join(', ')
  const overflow = names.length - ROLES_SHOWN
  return <>{overflow > 0 ? `${shown} +${overflow}` : shown}</>
}

/**
 * Entity type name. SUMMARY dashboards are the only ones scoped to an entity type.
 *
 * Gated the same way as {@link RolesCell}: `entityType` is not in the list response yet, so the
 * lookup stays dormant until it is.
 */
function EntityTypeCell({ dashboard }: { dashboard: DashboardListItem }) {
  const scoped = dashboard.type === DashboardType.SUMMARY && Boolean(dashboard.entityType)
  const { data: entityTypes } = useQuery({ ...DashboardQueries.entityTypes(), enabled: scoped })
  if (!scoped) return <>{EM_DASH}</>

  const name = entityTypes?.find((entityType) => entityType.id === dashboard.entityType)?.name
  return <>{name ?? EM_DASH}</>
}

/**
 * The page that shows an Insights dashboard to users. The other types have no page of their own:
 * homepage dashboards appear on the home page and summary dashboards on an entity's summary.
 */
function insightsDashboardUrl(dashboardId: number) {
  return new URL(`/insights/${dashboardId}`, window.location.origin).href
}

/** Copies the link to an Insights dashboard's page and says whether that worked. */
function useCopyInsightsLink() {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()

  return async (dashboardId: number) => {
    try {
      // navigator.clipboard is undefined outside a secure context, which the catch also covers.
      await navigator.clipboard.writeText(insightsDashboardUrl(dashboardId))
      enqueueSnackbar(t('dashboards.table.linkCopied'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('dashboards.table.copyLinkFailed'), { variant: 'error' })
    }
  }
}

function DashboardActionMenu({
  dashboard,
  onDelete,
}: {
  dashboard: DashboardListItem
  onDelete: () => void
}) {
  const { t } = useTranslation('admin-console')
  const navigate = useNavigate()
  const copyInsightsLink = useCopyInsightsLink()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('dashboards.table.actions.menu')}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            void navigate({
              to: '/preferences/dashboards/$dashboardId',
              params: { dashboardId: String(dashboard.id) },
            })
          }}
        >
          {t('dashboards.table.actions.edit')}
        </MenuItem>
        {/* In the menu rather than on the row: few users need it, and it takes no extra space. */}
        {dashboard.type === DashboardType.INSIGHTS && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              void copyInsightsLink(dashboard.id)
            }}
          >
            {t('dashboards.table.actions.copyLink')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            onDelete()
          }}
        >
          {t('dashboards.table.actions.delete')}
        </MenuItem>
      </Menu>
    </>
  )
}

function DashboardRow({
  dashboard,
  onDelete,
}: {
  dashboard: DashboardListItem
  onDelete: (dashboard: DashboardListItem) => void
}) {
  const { t } = useTranslation('admin-console')
  return (
    <TableRow>
      {/* Beside the cell, level with its row: below, the tooltip would cover the next row and sit
          off-center from a short name in this wide column. */}
      <OverflowCell
        tooltip={t('dashboards.table.id', { id: dashboard.id })}
        tooltipPlacement="right"
      >
        {dashboard.name}
      </OverflowCell>
      <TableCell>
        <TypeBadge type={dashboard.type} subType={dashboard.subType} />
      </TableCell>
      <TableCell>
        <RolesCell roleIds={dashboard.roleIds} />
      </TableCell>
      <TableCell>
        <EntityTypeCell dashboard={dashboard} />
      </TableCell>
      <TableCell>
        <LastUpdated value={dashboard.lastWrittenDate} />
      </TableCell>
      <TableCell align="right">
        <DashboardActionMenu dashboard={dashboard} onDelete={() => onDelete(dashboard)} />
      </TableCell>
    </TableRow>
  )
}

function filterDashboards(dashboards: DashboardListItem[], search: string) {
  const term = search.toLowerCase().trim()
  if (!term) return dashboards
  return dashboards.filter((dashboard) => dashboard.name.toLowerCase().includes(term))
}

interface DashboardsTableProps {
  search: string
  /** Undefined shows every type; the API applies the filter, so this changes the query key. */
  type?: DashboardType
}

/**
 * One grid for dashboards of every type, replacing the four separate legacy admin screens.
 *
 * The rows arrive already ordered by last-written date descending, so no client-side sort. Search
 * is client-side; the type filter is served by the API.
 */
export function DashboardsTable({ search, type }: DashboardsTableProps) {
  const { t } = useTranslation('admin-console')
  const { data: dashboards } = useSuspenseQuery(DashboardQueries.list(type))
  const [pendingDelete, setPendingDelete] = useState<DashboardListItem | null>(null)
  const filtered = filterDashboards(dashboards, search)

  return (
    <>
      <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell>{t('dashboards.table.columns.name')}</TableCell>
              <TableCell>{t('dashboards.table.columns.type')}</TableCell>
              <TableCell>{t('dashboards.table.columns.roles')}</TableCell>
              <TableCell>{t('dashboards.table.columns.entityType')}</TableCell>
              <TableCell>{t('dashboards.table.columns.lastUpdated')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('dashboards.table.noResults')}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((dashboard) => (
                <DashboardRow
                  key={dashboard.id}
                  dashboard={dashboard}
                  onDelete={setPendingDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <DeleteDashboardDialog dashboard={pendingDelete} onClose={() => setPendingDelete(null)} />
    </>
  )
}
